/**
 * Transparent short-TTL cache for backend GET requests + login-time prefetch.
 *
 * Why: the DB sits ~190ms away from the API server, so even optimized
 * endpoints cost 0.7-1s. Caching recent GET responses in the browser makes
 * tab switches / re-visits render instantly; any write (POST/PUT/PATCH/
 * DELETE) clears the cache so the user's own changes always show fresh.
 */
import axios from "axios";

const TTL_MS = 25000;
const MAX_ENTRIES = 100;
const cache = new Map(); // uri -> { t, data, headers }
let realAdapter = null;

const clone = (v) => {
  try { return structuredClone(v); } catch { return JSON.parse(JSON.stringify(v)); }
};

export function installApiCache() {
  if (realAdapter) return;
  realAdapter = axios.getAdapter(axios.defaults.adapter || ["xhr", "http"]);

  axios.defaults.adapter = async (config) => {
    const method = (config.method || "get").toLowerCase();
    const uri = axios.getUri(config);
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const isOurs = uri.includes("/api/") && (uri.startsWith("/api") || (backend && uri.startsWith(backend)));
    if (!isOurs) return realAdapter(config);

    if (method !== "get") {
      // A write anywhere may change what any list shows — drop everything.
      cache.clear();
      return realAdapter(config);
    }
    if (config.headers?.["x-no-cache"] || config.headers?.get?.("x-no-cache")) return realAdapter(config);

    const hit = cache.get(uri);
    const now = Date.now();
    if (hit && now - hit.t < TTL_MS) {
      return {
        data: clone(hit.data),
        status: 200,
        statusText: "OK",
        headers: hit.headers,
        config,
        request: {},
      };
    }
    const resp = await realAdapter(config);
    if (resp.status === 200) {
      if (cache.size >= MAX_ENTRIES) {
        cache.delete(cache.keys().next().value);
      }
      cache.set(uri, { t: now, data: clone(resp.data), headers: resp.headers });
    }
    return resp;
  };
}

export function clearApiCache() {
  cache.clear();
}

/** Warm the cache with the hot endpoints right after login. */
export function prefetchApi(paths) {
  for (const p of paths) {
    axios.get(p).catch(() => {});
  }
}
